import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth/AuthContext';
import { useToast } from '../components/ui/Toast';
import ConfirmModal from '../components/ui/ConfirmModal';

export default function ApiKeysPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const toast = useToast();

  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKey, setNewKey] = useState(null); // 생성된 키 (한 번만 표시)
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadKeys = async () => {
    try {
      const response = await fetch('/api/keys', { credentials: 'include' });
      const result = await response.json();
      if (result.success) {
        setKeys(result.data);
      }
    } catch (error) {
      console.error('Load keys error:', error);
      toast.error('API 키 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const handleCreate = async () => {
    if (keys.length >= 5) {
      toast.warning('최대 5개의 API 키만 생성할 수 있습니다.');
      return;
    }

    setCreating(true);
    try {
      const response = await fetch('/api/keys', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newKeyName || 'Default' }),
      });
      const result = await response.json();

      if (result.success) {
        setNewKey(result.data.api_key);
        setNewKeyName('');
        toast.success('API 키가 생성되었습니다.');
        loadKeys();
      } else {
        toast.error(result.error || 'API 키 생성 실패');
      }
    } catch (error) {
      console.error('Create key error:', error);
      toast.error('API 키 생성 중 오류가 발생했습니다.');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const response = await fetch(`/api/keys?key_id=${deleteTarget.key_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const result = await response.json();

      if (result.success) {
        toast.success('API 키가 삭제되었습니다.');
        loadKeys();
      } else {
        toast.error(result.error || 'API 키 삭제 실패');
      }
    } catch (error) {
      console.error('Delete key error:', error);
      toast.error('API 키 삭제 중 오류가 발생했습니다.');
    } finally {
      setDeleteTarget(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('클립보드에 복사되었습니다.');
  };

  return (
    <div className="page-with-nav" style={{
      minHeight: '100vh',
      background: 'var(--gradient-bg)',
      padding: 'var(--space-lg)',
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: 'var(--space-lg)',
        }}>
          <button
            onClick={() => navigate('/')}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: 'white',
            }}
          >
            ←
          </button>
          <h1 style={{
            fontSize: 'var(--font-size-xl)',
            color: 'white',
            margin: 0,
          }}>
            🔑 API 키 관리
          </h1>
        </div>

        {/* Create new key */}
        <div style={{
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-lg)',
          marginBottom: 'var(--space-lg)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <h2 style={{
            fontSize: 'var(--font-size-lg)',
            marginBottom: 'var(--space-md)',
            color: 'var(--color-text)',
          }}>
            새 API 키 생성
          </h2>

          <div style={{ display: 'flex', gap: 'var(--space-sm)', marginBottom: 'var(--space-md)' }}>
            <input
              type="text"
              placeholder="키 이름 (선택)"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e.target.value)}
              style={{
                flex: 1,
                padding: '0.75rem',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
              }}
            />
            <button
              onClick={handleCreate}
              disabled={creating || keys.length >= 5}
              style={{
                padding: '0.75rem 1.5rem',
                background: creating || keys.length >= 5 ? '#ccc' : 'var(--color-accent)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: creating || keys.length >= 5 ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {creating ? '생성 중...' : '생성'}
            </button>
          </div>

          <p style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            margin: 0,
          }}>
            최대 5개까지 생성 가능 ({keys.length}/5)
          </p>

          {/* Newly created key display */}
          {newKey && (
            <div style={{
              marginTop: 'var(--space-md)',
              padding: 'var(--space-md)',
              background: '#e8f5e9',
              borderRadius: 'var(--radius-md)',
              border: '1px solid #a5d6a7',
            }}>
              <p style={{
                fontSize: 'var(--font-size-sm)',
                color: '#2e7d32',
                fontWeight: 600,
                marginBottom: 'var(--space-sm)',
              }}>
                API 키가 생성되었습니다. 이 키는 다시 표시되지 않으니 안전하게 저장하세요.
              </p>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-sm)',
              }}>
                <code style={{
                  flex: 1,
                  padding: '0.5rem',
                  background: 'white',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                }}>
                  {newKey}
                </code>
                <button
                  onClick={() => copyToClipboard(newKey)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    background: '#2e7d32',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  복사
                </button>
              </div>
              <button
                onClick={() => setNewKey(null)}
                style={{
                  marginTop: 'var(--space-sm)',
                  padding: '0.5rem 1rem',
                  background: 'none',
                  color: '#666',
                  border: '1px solid #ccc',
                  borderRadius: 'var(--radius-md)',
                  cursor: 'pointer',
                  fontSize: 'var(--font-size-xs)',
                }}
              >
                확인 (닫기)
              </button>
            </div>
          )}
        </div>

        {/* Key list */}
        <div style={{
          background: 'white',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-lg)',
          boxShadow: 'var(--shadow-lg)',
        }}>
          <h2 style={{
            fontSize: 'var(--font-size-lg)',
            marginBottom: 'var(--space-md)',
            color: 'var(--color-text)',
          }}>
            내 API 키
          </h2>

          {loading ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-lg)' }}>
              로딩 중...
            </p>
          ) : keys.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center', padding: 'var(--space-lg)' }}>
              생성된 API 키가 없습니다.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
              {keys.map((key) => (
                <div
                  key={key.key_id}
                  style={{
                    padding: 'var(--space-md)',
                    background: 'var(--color-surface-dim)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 'var(--space-sm)',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <div style={{
                      fontWeight: 600,
                      color: 'var(--color-text)',
                      marginBottom: '4px',
                    }}>
                      {key.name}
                    </div>
                    <code style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontFamily: 'monospace',
                    }}>
                      {key.api_key_masked}
                    </code>
                    <div style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginTop: '4px',
                    }}>
                      생성: {new Date(key.created_at).toLocaleDateString('ko-KR')}
                      {key.last_used_at && ` | 마지막 사용: ${new Date(key.last_used_at).toLocaleDateString('ko-KR')}`}
                    </div>
                  </div>
                  <button
                    onClick={() => setDeleteTarget(key)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: 'var(--color-danger)',
                      color: 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    삭제
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Usage guide */}
        <div style={{
          background: 'rgba(255,255,255,0.1)',
          borderRadius: 'var(--radius-xl)',
          padding: 'var(--space-lg)',
          marginTop: 'var(--space-lg)',
          color: 'white',
        }}>
          <h3 style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-sm)' }}>
            API 사용 방법
          </h3>
          <p style={{ fontSize: 'var(--font-size-sm)', opacity: 0.8, marginBottom: 'var(--space-md)' }}>
            HTTP 요청 헤더에 <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>X-API-Key</code>를 추가하세요.
          </p>
          <pre style={{
            background: 'rgba(0,0,0,0.3)',
            padding: 'var(--space-md)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-xs)',
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>
{`curl -X GET "https://snap.wizice.com/api/snap/records" \\
  -H "X-API-Key: sk_your_api_key_here"`}
          </pre>
        </div>
      </div>

      {/* Delete confirm modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        title="API 키 삭제"
        message={`"${deleteTarget?.name}" API 키를 삭제하시겠습니까? 이 키를 사용하는 모든 연동이 중단됩니다.`}
        confirmText="삭제"
        danger
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
